import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/ui/design-system/components/ui/dialog';
import { useEffect, useRef } from 'react';
import { Button } from '@/ui/design-system/components/ui/button';
import { CheckmarkIcon } from 'react-hot-toast';
import { FLAG_STATUSES } from '@/config/constants';
import { Input } from '@/ui/design-system/components/ui/input';
import { Label } from '@/ui/design-system/components/ui/label';
import { Textarea } from '@/ui/design-system/components/ui/textarea';
import { mixpanelTrack } from 'utils/app';
import { useSubmitFlag } from 'utils/hooks/flags';
import useCurrentCountry from 'utils/hooks/useCurrentCountry';
import { useMutation } from 'react-query';
import { v4 as uuid } from 'uuid';

function SuggestDialog({ open, onOpenChange, defaultSuggestion = '' }) {
    const { mutateAsync: submitFlag } = useSubmitFlag();
    const currentCountry = useCurrentCountry();
    const countryId = currentCountry?.id;

    const {
        mutateAsync: suggest,
        status,
        reset,
        data,
    } = useMutation(async ({ suggestion, comment }) => {
        mixpanelTrack('Click', { context: 'SuggestButton' });
        await submitFlag({
            id: uuid(),
            status: FLAG_STATUSES.PENDING,
            data: { noResultsQuery: suggestion, comment, countryId },
        });
        return suggestion;
    });

    useEffect(() => {
        if (open) reset();
    }, [open]);

    useEffect(() => {
        if (status === 'success') {
            const timer = setTimeout(() => onOpenChange(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [status, onOpenChange]);

    const isLoading = status === 'loading';

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        suggest({
            suggestion: formData.get('suggestion'),
            comment: formData.get('comment'),
        });
    };

    if (status === 'success') {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent aria-describedby={undefined}>
                    <div className="flex flex-col items-center gap-3 py-4 text-center">
                        <CheckmarkIcon />
                        <DialogTitle>Thanks!</DialogTitle>
                        <p className="text-sm text-muted-foreground">
                            You suggested <strong>&ldquo;{data}&rdquo;</strong>.
                            Our team will review and any change will be
                            reflected in the next data update.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Suggest a trend</DialogTitle>
                        <DialogDescription>
                            Can&apos;t find a brand, industry, or ingredient?
                            Let us know and we&apos;ll try to include it in our
                            next update.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="suggestion">Suggestion</Label>
                            <Input
                                id="suggestion"
                                name="suggestion"
                                placeholder="Type a brand, industry, or ingredient"
                                defaultValue={defaultSuggestion}
                                key={defaultSuggestion}
                                disabled={isLoading}
                                required
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="comment">
                                Comment{' '}
                                <span className="font-normal text-muted-foreground">
                                    (optional)
                                </span>
                            </Label>
                            <Textarea
                                id="comment"
                                name="comment"
                                placeholder="Add context or additional suggestions"
                                disabled={isLoading}
                                rows={3}
                            />
                        </div>
                        {status === 'error' && (
                            <p className="text-sm text-destructive">
                                Unable to send suggestion. Please try again.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? 'Sending...' : 'Send Suggestion'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default SuggestDialog;
